import React, { useMemo } from 'react';
import {
  Link,
  DateRangeFilter,
  ErrorHandler,
  ExternalLink,
  Gutter,
  NumberRangeFilter,
  StatePill,
  StatusBadge,
  Text,
  TextFilterSearch,
} from '@common/components';

import { RolesQuery } from '@clinician-profile/graphql';
import {
  InputMaybe,
  MissionControlApprovalChainOrderField,
  MissionControlPaginatedApprovalChainsQueryCgResponse,
  OrderDirection,
} from '@common/graphql';
import { useLazyQuery, usePaginationHelper, useQuery } from '@common/hooks';
import {
  MISSION_CONTROL_APPROVAL_CHAINS_PENDING_QUERY,
  MissionControlStaffingRequestApprovableType,
  MissionControlStaffingRequestApprovalChainType,
  ORGANIZATION_BUSINESSES_QUERY,
  WORKS_CLINICAL_UNITS_QUERY,
} from '@institutions/graphql';
import { calculateTimeThresholds } from '@institutions/services';
import { JobActionsDropdown } from '@jobs/components';
import {
  buildOptionsFromData,
  FilteredTable,
  MultiSelectTableFilter,
} from 'works/components/filtered-table';
import { RenderTableColumn } from 'works/components/filtered-table/render-filtered-table';
import {
  toHumanDateTimeWithTZ,
  toUSCompactDateFormat,
} from 'works/legacy/lib/date-helper';

const LOCAL_STORAGE_KEY = 'MissionControlApprovalQueueTable';

type PendingApprovalStaffingRequestType =
  MissionControlStaffingRequestApprovableType & {
    createdAt: string;
  };

export function MissionControlStaffingTableApprovalQueue() {
  const {
    filters,
    paginationVariables,
    localStorageFilterKey,
    tableChangeHandler,
    filterChangeHandler,
  } = usePaginationHelper({
    localStorageFilterKey: LOCAL_STORAGE_KEY,
    defaultPaginationData: {
      defaultPageSize: 10,
      limit: 10,
      offset: 0,
      orderDirection: 'ASC',
      orderField: 'START_DATE',
    },
    columnsDictionary: {
      jobId: 'JOB_ID',
      role: 'ROLE',
      startDate: 'START_DATE',
      facility: 'FACILITY',
      unit: 'UNIT',
      positions: 'POSITIONS',
    },
  });

  const [fetchOrgBusiness, { data: orgBusinessData, error: orgBusinessError }] =
    useLazyQuery(ORGANIZATION_BUSINESSES_QUERY);

  const [fetchRoles, { data: rolesData, error: rolesError }] =
    useLazyQuery(RolesQuery);

  const [
    fetchClinicalUnits,
    { data: clinicalUnitData, error: clinicalUnitError },
  ] = useLazyQuery(WORKS_CLINICAL_UNITS_QUERY);

  const { data, loading, error } = useQuery(
    MISSION_CONTROL_APPROVAL_CHAINS_PENDING_QUERY,
    {
      variables: {
        limit: paginationVariables.limit,
        offset: paginationVariables.offset,
        orderField:
          paginationVariables.orderField as InputMaybe<MissionControlApprovalChainOrderField>,
        orderDirection:
          paginationVariables.orderDirection as InputMaybe<OrderDirection>,
        filters: paginationVariables.filters,
      },
      notifyOnNetworkStatusChange: true,
      onCompleted() {
        fetchOrgBusiness();
        fetchRoles();
        fetchClinicalUnits();
      },
    }
  );

  const pendingApprovalStaffingRequests = useMemo(() => {
    if (!data?.missionControlPaginatedApprovalChains) {
      return [];
    }

    const chains = (data.missionControlPaginatedApprovalChains.approvalChains ||
      []) as NonNullable<
      MissionControlPaginatedApprovalChainsQueryCgResponse['missionControlPaginatedApprovalChains']['approvalChains']
    >;

    return chains
      .filter((chain) => chain.approvable.__typename === 'StaffingRequest')
      .map((chain) => {
        const { approvable } =
          (chain as MissionControlStaffingRequestApprovalChainType) || {};

        return {
          ...approvable,
          createdAt: chain.createdAt,
        } satisfies PendingApprovalStaffingRequestType;
      });
  }, [data]);

  const totalCount =
    data?.missionControlPaginatedApprovalChains?.totalCount || 0;

  const columns: RenderTableColumn<PendingApprovalStaffingRequestType>[] = [
    {
      title: 'Job ID',
      dataIndex: 'code',
      key: 'jobId',
      width: 140,
      sorter: true,
      filtered: !!filters.jobId,
      filteredValue: filters.jobId || null,
      filterDropdown: ({ setSelectedKeys, confirm }) => (
        <Gutter internal size="md">
          <TextFilterSearch
            onSearch={(value) => {
              value ? setSelectedKeys([value]) : setSelectedKeys([]);
              confirm();
            }}
          />
        </Gutter>
      ),
      render: (_: unknown, record: PendingApprovalStaffingRequestType) => (
        <Link to={`/works/staffing_requests/${record.id}`} target="_blank">
          <Text bold color="elm">
            {record.code}
          </Text>
        </Link>
      ),
    },
    {
      title: 'Role',
      dataIndex: ['role', 'abbreviation'],
      key: 'role',
      width: 140,
      sorter: true,
      filtered: !!filters.role,
      filteredValue: filters.role || null,
      filters:
        rolesData?.roles &&
        buildOptionsFromData(
          rolesData.roles,
          (role: { id: string; abbreviation: string }) => ({
            text: role.abbreviation,
            value: role.id,
          })
        ),
      filterDropdown: MultiSelectTableFilter,
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
      width: 87,
      sorter: true,
      filterFormat: 'dateRange',
      filtered: !!filters.startDate,
      filteredValue: filters.startDate || null,
      filterDropdown: ({ setSelectedKeys, confirm }) => (
        <Gutter internal size="md">
          <DateRangeFilter
            autoFocus
            onSearch={(value) => {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              value ? setSelectedKeys(value as any) : setSelectedKeys([]);
              confirm();
            }}
          />
        </Gutter>
      ),
      render: (startDate: string) => {
        return startDate && toUSCompactDateFormat(startDate);
      },
    },
    {
      title: 'Request Age',
      dataIndex: 'createdAt',
      key: 'requestAge',
      width: 130,
      render: (createdAt: string) => {
        const requestAge = calculateRequestAge(createdAt);
        const requestAgeInfo = `Requested on ${toHumanDateTimeWithTZ(createdAt)}`;

        return (
          <StatusBadge
            info={requestAgeInfo}
            type={requestAge?.type}
            value={requestAge.formattedAge}
          />
        );
      },
    },
    {
      title: 'Facility',
      dataIndex: [
        'organizationUnit',
        'department',
        'organizationBusiness',
        'name',
      ],
      key: 'facility',
      sorter: true,
      filtered: !!filters.facility,
      filteredValue: filters.facility || null,
      filters: buildOptionsFromData(
        orgBusinessData?.organizationBusinesses || [],
        (organizationBusiness: { id: string; name: string }) => ({
          value: organizationBusiness.id,
          text: organizationBusiness.name,
        })
      ),
      filterDropdown: MultiSelectTableFilter,
    },
    {
      title: 'Unit',
      dataIndex: ['organizationUnit', 'clinicalUnit', 'displayName'],
      key: 'unit',
      sorter: true,
      filtered: !!filters.unit,
      filteredValue: filters.unit || null,
      filters:
        clinicalUnitData &&
        buildOptionsFromData(
          clinicalUnitData.worksClinicalUnits,
          (worksClinicalUnit: { id: string; displayName: string }) => ({
            value: worksClinicalUnit.id,
            text: worksClinicalUnit.displayName,
          })
        ),
      filterDropdown: MultiSelectTableFilter,
    },
    {
      title: 'Positions',
      dataIndex: 'quantity',
      key: 'positions',
      width: 100,
      sorter: true,
      filtered: !!filters.positions,
      filteredValue: filters.positions || null,
      filterDropdown: ({ setSelectedKeys, confirm }) => (
        <Gutter internal size="md">
          <NumberRangeFilter
            onSearch={(value) => {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              value ? setSelectedKeys(value as any) : setSelectedKeys([]);
              confirm();
            }}
          />
        </Gutter>
      ),
    },
    {
      title: 'Job Status',
      dataIndex: 'state',
      key: 'jobStatus',
      width: 112,
      render: (status: string) => (
        <StatePill capitalized color="steel-lighter">
          {status.toLowerCase()}
        </StatePill>
      ),
    },
    {
      title: 'Action',
      render: (_: unknown, job: PendingApprovalStaffingRequestType) => (
        <JobActionsDropdown
          job={job}
          refetchQueries={['MissionControlPaginatedApprovalChains']}
        />
      ),
    },
  ];

  const filtersDataReady =
    !!orgBusinessData && !!rolesData && !!clinicalUnitData;

  const anyError = error || orgBusinessError || rolesError || clinicalUnitError;

  if (anyError) {
    return (
      <ErrorHandler
        error={anyError}
        log="MissionControlStaffingTableApprovalQueue"
      />
    );
  }

  const pageCount = calcPageCount(
    totalCount,
    paginationVariables.limit,
    paginationVariables.current
  );

  return (
    <div>
      <FilteredTable
        rowKey="key"
        columns={columns}
        dataSource={pendingApprovalStaffingRequests}
        pagination={{ ...paginationVariables, total: totalCount }}
        loading={loading}
        filtersDataReady={filtersDataReady}
        onChange={tableChangeHandler}
        onFilterChange={filterChangeHandler}
        filters={filters}
        localStorageFilterKey={localStorageFilterKey}
      />
      <Gutter size="sm" direction="vertical">
        Showing {pageCount} of {totalCount}.{' '}
        <ExternalLink url="/works/staffing_requests">View All</ExternalLink>.
      </Gutter>
    </div>
  );
}

function calcPageCount(totalCount = 0, pageSize = 10, currentPage = 1) {
  return pageSize * currentPage > totalCount ? totalCount % pageSize : pageSize;
}

function calculateRequestAge(createdAt: string) {
  return calculateTimeThresholds({
    createdAt,
    dangerThresholdInHours: 48,
    warningThresholdInHours: 24,
  });
}
